<script setup lang="ts">
import type { HTMLAttributes } from 'vue';
import { ref, nextTick } from 'vue';
import { reactiveOmit } from '@vueuse/core';
import type { RadioGroupRootEmits, RadioGroupRootProps } from 'reka-ui';
import { RadioGroupRoot, useForwardPropsEmits } from 'reka-ui';
import { cn } from '@/lib/utils';

const props = defineProps<
    RadioGroupRootProps & { class?: HTMLAttributes['class'] }
>();
const emits = defineEmits<RadioGroupRootEmits>();

const delegatedProps = reactiveOmit(props, 'class');

const forwarded = useForwardPropsEmits(delegatedProps, emits);

const rootRef = ref<InstanceType<typeof RadioGroupRoot>>();

// Formie's conditions.js listens for `input` events on hidden inputs, but reka-ui
// dispatches `change`. Bridge the gap so Formie re-evaluates conditions on selection change.
function onValueChange() {
    nextTick(() => {
        const hiddenInput = rootRef.value?.$el?.querySelector('input[type="hidden"]');
        hiddenInput?.dispatchEvent(new Event('input', { bubbles: true }));
    });
}
</script>

<template>
    <RadioGroupRoot
        ref="rootRef"
        v-slot="slotProps"
        data-slot="radio-group"
        :class="cn('grid gap-2', props.class)"
        v-bind="forwarded"
        @update:model-value="onValueChange"
    >
        <slot v-bind="slotProps" />
    </RadioGroupRoot>
</template>
